<template>
  <article
    class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition hover:border-green-100 hover:shadow-md"
  >
    <div class="flex flex-col gap-4 lg:flex-row">
      <div
        class="flex h-40 w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-gray-100 bg-gray-50 lg:w-52"
      >
        <img
          v-if="photoSrc"
          :src="photoSrc"
          :alt="item.title || 'Foto do item'"
          class="h-full w-full object-cover"
          @error="imageHasError = true"
        />

        <div v-else class="text-center">
          <PhImageSquare class="mx-auto h-10 w-10 text-gray-300" />
          <p class="mt-2 text-xs text-gray-400">Sem imagem</p>
        </div>
      </div>

      <div class="min-w-0 flex-1">
        <div
          class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
        >
          <div>
            <div class="mb-2 flex flex-wrap items-center gap-2">
              <span
                class="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-500"
              >
                #{{ item.order || index + 1 }}
              </span>

              <span
                class="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700"
              >
                {{ getItemTypeLabel(item.item_type) }}
              </span>

              <span
                v-if="item.risk_level"
                class="rounded-full px-3 py-1 text-xs font-semibold"
                :class="getRiskClass(item.risk_level)"
              >
                {{ getRiskLabel(item.risk_level) }}
              </span>

              <span
                v-if="item.priority"
                class="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700"
              >
                {{ getPriorityLabel(item.priority) }}
              </span>
            </div>

            <h3 class="text-base font-bold text-gray-900 text-start">
              {{ item.title || "Item sem título" }}
            </h3>

            <div
              class="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 text-start"
            >
              <span v-if="item.sector" class="inline-flex items-center gap-1">
                <PhBuildings class="h-4 w-4" />
                {{ item.sector }}
              </span>

              <span v-if="item.location" class="inline-flex items-center gap-1">
                <PhMapPin class="h-4 w-4" />
                {{ item.location }}
              </span>
            </div>
          </div>

          <div class="flex shrink-0 items-center gap-1">
            <button
              type="button"
              class="rounded-lg p-2 text-gray-400 transition"
              :class="
                index === 0 || readOnly
                  ? 'cursor-not-allowed opacity-30'
                  : 'hover:bg-green-50 hover:text-green-700'
              "
              :disabled="index === 0 || readOnly"
              title="Mover para cima"
              @click="$emit('move-up', item)"
            >
              <PhCaretUp class="h-5 w-5" />
            </button>

            <button
              type="button"
              class="rounded-lg p-2 text-gray-400 transition"
              :class="
                index === total - 1 || readOnly
                  ? 'cursor-not-allowed opacity-30'
                  : 'hover:bg-green-50 hover:text-green-700'
              "
              :disabled="index === total - 1 || readOnly"
              title="Mover para baixo"
              @click="$emit('move-down', item)"
            >
              <PhCaretDown class="h-5 w-5" />
            </button>

            <button
              type="button"
              class="rounded-lg p-2 text-gray-400 transition"
              :class="
                readOnly
                  ? 'cursor-not-allowed opacity-30'
                  : 'hover:bg-yellow-50 hover:text-yellow-600'
              "
              :disabled="readOnly"
              title="Editar item"
              @click="$emit('edit', item)"
            >
              <PhNotePencil class="h-5 w-5" />
            </button>

            <button
              type="button"
              class="rounded-lg p-2 text-gray-400 transition"
              :class="
                readOnly
                  ? 'cursor-not-allowed opacity-30'
                  : 'hover:bg-red-50 hover:text-red-600'
              "
              :disabled="readOnly"
              title="Remover item"
              @click="$emit('remove', item)"
            >
              <PhTrash class="h-5 w-5" />
            </button>
          </div>
        </div>

        <div class="mt-4 grid grid-cols-1 gap-3 text-sm text-start">
          <div v-if="item.description">
            <p class="font-semibold text-gray-700">Observação</p>
            <p class="mt-1 whitespace-pre-line text-gray-500">
              {{ item.description }}
            </p>
          </div>

          <div v-if="item.improvement_point">
            <p class="font-semibold text-gray-700">Ponto de melhoria</p>
            <p class="mt-1 whitespace-pre-line text-gray-500">
              {{ item.improvement_point }}
            </p>
          </div>

          <div v-if="item.recommendation">
            <p class="font-semibold text-gray-700">Recomendação</p>
            <p class="mt-1 whitespace-pre-line text-gray-500">
              {{ item.recommendation }}
            </p>
          </div>

          <div v-if="item.technical_observation">
            <p class="font-semibold text-gray-700">Observação técnica</p>
            <p class="mt-1 whitespace-pre-line text-gray-500">
              {{ item.technical_observation }}
            </p>
          </div>
        </div>

        <div
          v-if="!hasTechnicalContent"
          class="mt-4 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-400"
        >
          Nenhuma observação, ponto de melhoria ou recomendação foi preenchida
          para este item.
        </div>

      </div>
    </div>
  </article>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import {
  PhBuildings,
  PhCaretDown,
  PhCaretUp,
  PhImageSquare,
  PhMapPin,
  PhNotePencil,
  PhTrash,
} from "@phosphor-icons/vue";

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    default: 0,
  },
  total: {
    type: Number,
    default: 0,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["edit", "remove", "move-up", "move-down"]);

const imageHasError = ref(false);

const photoSrc = computed(() => {
  if (imageHasError.value) return "";

  if (props.item.photo_url) {
    return props.item.photo_url;
  }

  if (props.item.photo_path) {
    return buildFileUrl(props.item.photo_path);
  }

  return "";
});

const hasTechnicalContent = computed(() => {
  return [
    props.item.description,
    props.item.improvement_point,
    props.item.recommendation,
    props.item.technical_observation,
  ]
    .filter(Boolean)
    .some((field) => String(field).trim().length > 0);
});

watch(
  () => [props.item.photo_url, props.item.photo_path],
  () => {
    imageHasError.value = false;
  }
);

function buildFileUrl(path) {
  const baseUrl =
    process.env.VUE_APP_FILES_URL || process.env.VUE_APP_BASE_FILES_URL || process.env.VUE_APP_IMAGE_SERVE || "";

  if (!baseUrl || !path) return "";

  return `${baseUrl.replace(/\/$/, "")}/${String(path).replace(/^\//, "")}`;
}

function getItemTypeLabel(type) {
  const labels = {
    photo_observation: "Foto + observação",
    free_text: "Texto livre",
    checklist_answer: "Checklist",
    action_plan: "Plano de ação",
    section_title: "Seção",
    image_text: "Imagem + texto",
  };

  return labels[type] || "Item técnico";
}

function getRiskLabel(level) {
  const labels = {
    low: "Baixo",
    medium: "Moderado",
    substantial: "Substancial",
    serious: "Grave",
    critical: "Crítico",
    health_risk: "Risco à saúde",
    legal_risk: "Risco legal",
  };

  return labels[level] || "Risco";
}

function getPriorityLabel(priority) {
  const labels = {
    low: "Baixa",
    medium: "Média",
    high: "Alta",
    urgent: "Urgente",
  };

  return labels[priority] || "Prioridade";
}

function getRiskClass(level) {
  const classes = {
    low: "bg-gray-100 text-gray-600",
    medium: "bg-yellow-50 text-yellow-700",
    substantial: "bg-orange-50 text-orange-700",
    serious: "bg-red-50 text-red-700",
    critical: "bg-red-100 text-red-800",
    health_risk: "bg-purple-50 text-purple-700",
    legal_risk: "bg-blue-50 text-blue-700",
  };

  return classes[level] || "bg-gray-100 text-gray-600";
}
</script>
